import type { ComponentProps, ReactNode } from 'react';

import { css } from '@sorare/css';

import { Dropdown } from '#src/atoms/dropdowns/Dropdown/index.tsx';
import { Horizontal, Vertical } from '#src/atoms/layout/flex/index.tsx';
import { NavItem } from '#src/components/navigation/NavItem/index.tsx';
import { useIsLaptop } from '#src/hooks/device/useIsLaptop.ts';

const dropdownContent = css`
  background-color: var(--c-surface-background);
  padding: var(--half-unit);
`;

const subNavRoot = css`
  border-radius: var(--intermediate-unit);
  background-color: var(--c-surface-background);
`;

export const DropdownContent = ({ children }: { children: ReactNode }) => (
  <Horizontal className={dropdownContent}>{children}</Horizontal>
);

type ItemProps = ComponentProps<typeof NavItem> & {
  subItems?: ItemProps[];
};

export const NavItemWithSubNav = ({
  item,
  level = 0,
}: {
  item: ItemProps;
  level?: number;
}) => {
  const isLaptopAndAbove = useIsLaptop();

  const { subItems, to, ...mainItemProps } = item;
  if (isLaptopAndAbove && !!subItems?.length) {
    if (level > 0) {
      return (
        <>
          <NavItem to={to} {...mainItemProps} />
          <Vertical
            className={subNavRoot}
            gap={0.5}
            style={{ paddingLeft: `calc(${level} * var(--quadruple-unit))` }}
          >
            {subItems.map(subNavItem => (
              <NavItemWithSubNav
                key={subNavItem.label}
                item={subNavItem}
                level={level + 1}
              />
            ))}
          </Vertical>
        </>
      );
    }
    return (
      <Dropdown
        forceDesktopBehavior
        triggerOnHover
        side="right"
        gap={24}
        label={({ open, ...otherProps }) => (
          <menu {...otherProps} type="menu">
            <li>
              <NavItem to={to} {...mainItemProps} />
            </li>
          </menu>
        )}
      >
        {({ closeDropdown }) => (
          <DropdownContent>
            <Vertical className={subNavRoot} gap={0.5} onClick={closeDropdown}>
              {subItems.map(subNavItem => (
                <NavItemWithSubNav
                  key={subNavItem.label}
                  item={subNavItem}
                  level={level + 1}
                />
              ))}
            </Vertical>
          </DropdownContent>
        )}
      </Dropdown>
    );
  }

  return <NavItem to={to} {...mainItemProps} />;
};
